import { M_PER_DEG_LAT, type LatLng } from '@/core/utils';
import type { ElevationSampler } from '@/features/elevation';

export interface ElevationGrid {
  /** Row-major size×size elevations; row 0 is north, column 0 is west; origin in the centre cell. */
  elev: Float32Array;
  size: number;
  half: number;
  /** Range of elevations within the circular radius. */
  minElev: number;
  maxElev: number;
  originElev: number;
}

/** Samples a square grid of `cellSize` cells covering `radius` around the origin. */
export function sampleElevationGrid(
  sample: ElevationSampler,
  origin: LatLng,
  cellSize: number,
  radius: number,
  mPerDegLng: number,
): ElevationGrid {
  const half = Math.ceil(radius / cellSize);
  const size = 2 * half + 1;
  const elev = new Float32Array(size * size);
  let minElev = Infinity;
  let maxElev = -Infinity;
  for (let j = 0; j < size; j++) {
    const lat = origin.lat + ((half - j) * cellSize) / M_PER_DEG_LAT;
    for (let i = 0; i < size; i++) {
      const e = (elev[j * size + i] = sample(lat, origin.lng + ((i - half) * cellSize) / mPerDegLng));
      if ((i - half) ** 2 + (j - half) ** 2 <= half * half) {
        minElev = Math.min(minElev, e);
        maxElev = Math.max(maxElev, e);
      }
    }
  }
  const originElev = sample(origin.lat, origin.lng);
  elev[half * size + half] = originElev;
  return { elev, size, half, minElev, maxElev, originElev };
}

/** The centred sub-grid with the given half-size. */
export function cropElevationGrid(grid: ElevationGrid, half: number): Float32Array {
  const size = 2 * half + 1;
  const out = new Float32Array(size * size);
  for (let j = 0; j < size; j++) {
    const row = (j + grid.half - half) * grid.size + grid.half - half;
    out.set(grid.elev.subarray(row, row + size), j * size);
  }
  return out;
}
